"use client";

import { useEffect, useState } from "react";
import { trpc } from "@/lib/trpc/client";
import { cn } from "@/lib/utils";
import {
  IconInfoCircle,
  IconAlertTriangle,
  IconAlertOctagon,
  IconX,
} from "@tabler/icons-react";

const DISMISS_KEY = "dismissedAnnouncementId";

export type AnnouncementSeverity = "info" | "warning" | "critical";

// Modern, CarTracked-aligned treatment: "info" leans on the brand green,
// each severity gets a soft horizontal gradient wash, a tinted icon chip, and
// full dark-mode support. Kept as data so the admin composer can reuse it for a
// live preview.
const SEVERITY_STYLES: Record<
  AnnouncementSeverity,
  { container: string; chip: string; accent: string; icon: React.ReactNode }
> = {
  info: {
    container:
      "border-emerald-200/70 bg-gradient-to-r from-emerald-50 to-emerald-50/30 text-emerald-950 dark:border-emerald-900/50 dark:from-emerald-950/60 dark:to-emerald-950/20 dark:text-emerald-50",
    chip: "bg-emerald-100 text-emerald-600 dark:bg-emerald-900/60 dark:text-emerald-300",
    accent: "bg-emerald-500",
    icon: <IconInfoCircle className="size-3.5" />,
  },
  warning: {
    container:
      "border-amber-200/70 bg-gradient-to-r from-amber-50 to-amber-50/30 text-amber-950 dark:border-amber-900/50 dark:from-amber-950/60 dark:to-amber-950/20 dark:text-amber-50",
    chip: "bg-amber-100 text-amber-600 dark:bg-amber-900/60 dark:text-amber-300",
    accent: "bg-amber-500",
    icon: <IconAlertTriangle className="size-3.5" />,
  },
  critical: {
    container:
      "border-red-200/70 bg-gradient-to-r from-red-50 to-red-50/30 text-red-950 dark:border-red-900/50 dark:from-red-950/60 dark:to-red-950/20 dark:text-red-50",
    chip: "bg-red-100 text-red-600 dark:bg-red-900/60 dark:text-red-300",
    accent: "bg-red-500",
    icon: <IconAlertOctagon className="size-3.5" />,
  },
};

/**
 * Render a plain message with light inline formatting: newlines become line
 * breaks (via `whitespace-pre-line` on the container) and `**text**` renders
 * bold. Tokenized into React nodes rather than dangerouslySetInnerHTML, so the
 * message stays un-trusted-input safe. `split` with a capturing group yields
 * plain text at even indices and bold content at odd indices.
 */
function formatMessage(text: string): React.ReactNode[] {
  return text.split(/\*\*([\s\S]+?)\*\*/g).map((part, i) =>
    i % 2 === 1 ? (
      <strong key={i} className="font-semibold">
        {part}
      </strong>
    ) : (
      part
    ),
  );
}

/**
 * Presentational announcement bar. Stateless so it can back both the live
 * banner and the admin composer's preview. Pass `onDismiss` to show the close
 * button.
 */
export function AnnouncementBar({
  severity,
  message,
  onDismiss,
  className,
}: {
  severity: AnnouncementSeverity;
  message: string;
  onDismiss?: () => void;
  className?: string;
}) {
  const styles = SEVERITY_STYLES[severity] ?? SEVERITY_STYLES.info;

  return (
    <div
      role="status"
      className={cn(
        // Floating, self-contained card: inset by its own margin, rounded with
        // a soft shadow + backdrop blur so it reads as elevated above content,
        // while staying in flow (pushes content, never overlays it). Grows
        // gracefully with multi-line messages.
        "relative m-3 flex items-start gap-3 overflow-hidden rounded-xl border py-3 pl-5 pr-3 text-sm shadow-sm backdrop-blur-sm animate-fadeIn",
        styles.container,
        className,
      )}
    >
      {/* Severity accent strip down the left edge. */}
      <span className={cn("absolute inset-y-0 left-0 w-1", styles.accent)} />
      <span
        className={cn(
          "mt-px flex size-6 shrink-0 items-center justify-center rounded-full",
          styles.chip,
        )}
      >
        {styles.icon}
      </span>
      <span className="flex-1 whitespace-pre-line break-words self-center font-medium leading-relaxed">
        {formatMessage(message)}
      </span>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss announcement"
          className="shrink-0 rounded-md p-1 opacity-60 transition-all hover:bg-black/5 hover:opacity-100 dark:hover:bg-white/10"
        >
          <IconX className="size-4" />
        </button>
      )}
    </div>
  );
}

export function AnnouncementBanner() {
  // Refetch on an interval so a newly posted (or cleared) notice appears
  // without a full page reload — important during an incident.
  const { data: announcement } = trpc.system.activeAnnouncement.useQuery(
    undefined,
    {
      refetchInterval: 60_000,
      refetchOnWindowFocus: true,
    },
  );

  const [dismissedId, setDismissedId] = useState<string | null>(null);

  // Read the dismissed id from localStorage once mounted (avoids SSR mismatch).
  useEffect(() => {
    setDismissedId(localStorage.getItem(DISMISS_KEY));
  }, []);

  if (!announcement) return null;

  const isDismissed =
    announcement.dismissible && dismissedId === announcement.id;
  if (isDismissed) return null;

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, announcement.id);
    setDismissedId(announcement.id);
  };

  return (
    <AnnouncementBar
      severity={announcement.severity as AnnouncementSeverity}
      message={announcement.message}
      onDismiss={announcement.dismissible ? handleDismiss : undefined}
    />
  );
}
